import React from 'react';

import type { ReportPreviewColumn } from '@aline/common';
import { Chip, Divider, Typography } from '@mui/material';
import { ReportPreviewColumnRow } from 'src/components/ai/chat/ChatPanelMarkdown/elements/CodeElement/ReportPreviewColumnRow';
import { makeStyles } from 'tss-react/mui';

import { getInputColumnTitles } from './question-plan-helpers';
import { useReportPlanRowStyles } from '../ReportPlanRow';

const useStyles = makeStyles()(theme => ({
  container: {
    border: `1px solid ${theme.appColors.rule}`,
    borderRadius: 4,
    overflow: 'hidden',
  },
  existingTitle: {
    flex: 1,
    minWidth: 0,
  },
}));

interface QuestionColumnsEditorProps {
  disabled: boolean;
  /** Questions that will be created, or reused, on approval. The user can leave them out. */
  editableColumns: readonly ReportPreviewColumn[];
  excludedIndices: ReadonlySet<number>;
  onToggleExclude: (index: number) => void;
  /** Rows shown first that the card does not edit, such as a report's system columns. */
  leadingRows?: React.ReactNode[];
  /** Questions that already exist and run as they are. */
  readOnlyColumns?: readonly { id: string; title: string }[];
}

/** A question the run uses as it is: no draft to review, nothing to leave out. */
const ExistingQuestionRow = React.memo(function ExistingQuestionRow({ title }: { title: string }) {
  const { classes } = useStyles();
  const { classes: rowClasses } = useReportPlanRowStyles();
  return (
    <div className={rowClasses.row}>
      <div className={rowClasses.content}>
        <Typography className={classes.existingTitle} variant="subtitle2">
          {title}
        </Typography>
      </div>
      <Chip label="Existing" size="small" variant="outlined" />
    </div>
  );
});

/**
 * The questions of a question plan, as one bordered list: rows the card does
 * not edit, then questions that already exist, then the drafts and reused
 * questions the user can leave out. Shared by the report plan and the run
 * card so a question looks the same wherever it is approved.
 */
export const QuestionColumnsEditor = React.memo(function QuestionColumnsEditor({
  disabled,
  editableColumns,
  excludedIndices,
  leadingRows = [],
  onToggleExclude,
  readOnlyColumns = [],
}: QuestionColumnsEditorProps) {
  const { classes } = useStyles();
  const rows = [
    ...leadingRows,
    ...readOnlyColumns.map(question => (
      <ExistingQuestionRow key={question.id} title={question.title} />
    )),
    ...editableColumns.map((column, index) => (
      <ReportPreviewColumnRow
        key={column.key}
        column={column}
        disabled={disabled}
        excluded={excludedIndices.has(index)}
        inputTitles={getInputColumnTitles(column, editableColumns)}
        onToggleExclude={() => onToggleExclude(index)}
      />
    )),
  ];
  if (!rows.length) return null;

  return (
    <div className={classes.container}>
      {rows.map((row, index) => (
        <React.Fragment key={getRowKey(row, index)}>
          {index > 0 && <Divider />}
          {row}
        </React.Fragment>
      ))}
    </div>
  );
});

function getRowKey(row: React.ReactNode, index: number) {
  if (React.isValidElement(row) && row.key !== null) return row.key;
  return index;
}
